import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, User, LogOut, Shield, XCircle } from 'lucide-react';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useUserRole } from '../hooks/useUserRole';
import { useDisplayProfile } from '../hooks/useDisplayProfile';
import NotificationsDropdown from './NotificationsDropdown';
import AppearanceSelector from './AppearanceSelector';
import { getUnreadNotificationsCount } from '../lib/services/NotificationService';

const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const { isImpersonating, impersonatedEmail, stopImpersonation } = useImpersonation();
  const { isAdmin } = useUserRole();
  const { displayName, avatarUrl } = useDisplayProfile();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAvatarBroken(false);
  }, [avatarUrl]);

  useEffect(() => {
    const loadUnreadCount = async () => {
      if (user) {
        const { count } = await getUnreadNotificationsCount();
        setUnreadCount(count);
      }
    };
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!showNotifications && user) {
      getUnreadNotificationsCount().then(({ count }) => setUnreadCount(count));
    }
  }, [showNotifications, user]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSignOut = async () => {
    try {
      stopImpersonation();
    } catch {}
    await signOut();
  };

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        navigate(`/transactions?search=${encodeURIComponent(searchQuery.trim())}`);
        setSearchQuery('');
      }
    },
    [navigate, searchQuery],
  );

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const showPhoto = Boolean(avatarUrl) && !avatarBroken;

  return (
    <header className="relative z-10 border-b border-white/[0.06] bg-dark-960/70 px-5 py-3 backdrop-blur-xl md:px-7">
      <div className="flex items-center justify-between gap-4">
        <div className="mx-auto w-full max-w-xl flex-1">
          <form onSubmit={handleSearch} className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-dark-500" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Rechercher des transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-2xl border border-white/[0.06] bg-dark-970/80 py-2.5 pl-10 pr-16 text-sm text-white placeholder-dark-500 shadow-card transition focus:border-primary-500/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-semibold text-dark-400 sm:inline">
              {isMac ? '⌘' : 'Ctrl'} K
            </kbd>
          </form>
        </div>

        <div className="flex items-center gap-1.5">
          {isImpersonating && (
            <div className="mr-1 hidden items-center gap-1.5 rounded-xl border border-amber-700/60 bg-amber-900/40 px-2.5 py-1 text-body-sm text-amber-200 md:flex">
              <span>Impersonation: {impersonatedEmail}</span>
              <button onClick={stopImpersonation} className="hover:text-white" title="Quitter l'impersonation">
                <XCircle className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="hidden sm:block">
            <AppearanceSelector compact />
          </div>

          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`relative rounded-xl p-2 transition-all duration-200 ${
                unreadCount > 0
                  ? 'bg-primary-500/15 text-primary-400 ring-1 ring-primary-500/30'
                  : 'text-dark-400 hover:bg-white/[0.05] hover:text-white'
              }`}
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-dark-960 bg-primary-500 px-1 text-[10px] font-semibold text-onPrimary">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <NotificationsDropdown onClose={() => setShowNotifications(false)} />
            )}
          </div>

          <button
            onClick={() => navigate('/settings?tab=profile')}
            className="flex items-center gap-2.5 rounded-2xl px-2 py-1.5 transition-colors hover:bg-white/[0.04]"
          >
            <div className="hidden text-right sm:block">
              <p className="text-[13px] font-semibold leading-tight text-white">{displayName}</p>
              <p className="max-w-[150px] truncate text-[11px] text-dark-500">{user?.email}</p>
            </div>
            {showPhoto ? (
              <img
                src={avatarUrl!}
                alt=""
                className="h-8 w-8 rounded-full object-cover ring-1 ring-white/10"
                onError={() => setAvatarBroken(true)}
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 shadow-glow-green">
                <User className="h-3.5 w-3.5 text-onPrimary" />
              </div>
            )}
          </button>

          {isAdmin && (
            <button
              onClick={() => navigate('/admin/users')}
              className="rounded-xl p-2 text-dark-400 transition-colors hover:bg-white/[0.05] hover:text-white"
              title="Administration"
            >
              <Shield className="h-4 w-4" />
            </button>
          )}

          <button
            onClick={handleSignOut}
            className="rounded-xl p-2 text-dark-400 transition-colors hover:bg-white/[0.05] hover:text-red-400"
            title="Se déconnecter"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
